import { IconArrowLeft, IconArrowRight, IconCheck, IconSparkles } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useBlocker, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { StrategyDetailSheet } from "@/components/strategy-lab/StrategyDetailSheet";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { type StrategySnapshot, strategySnapshotQuery } from "@/lib/strategy-snapshot";
import { cn } from "@/lib/utils";
import {
  AUTO_EXPLORATION_STRATEGY,
  BUILTIN_STRATEGIES,
  GENERIC_TACTIC_IDS,
  type Strategy,
} from "@/mocks/operating-strategy";
import { STRATEGY_PRODUCT, STRATEGY_TACTIC_IDS } from "@/mocks/strategy-product";
import { useAuthStore } from "@/stores/auth";
import { operatingStrategyScope, useOperatingStrategyStore } from "@/stores/operating-strategy";
import { BrandAuthorizationDialog } from "./BrandAuthorizationDialog";
import { CompleteStep } from "./CompleteStep";
import { KeywordPreviewDialog } from "./KeywordPreviewDialog";
import { PlanStep } from "./PlanStep";
import { ReportLink } from "./ReportLink";
import { ResearchStep } from "./ResearchStep";
import { SettingsStep } from "./SettingsStep";
import { SALES_GOAL, useStrategyWizard } from "./use-strategy-wizard";

const STEPS = [
  ["赛道洞察", "了解商品与市场"],
  ["AI策略KPI目标设定", "确定目标与打法"],
  ["完成", "查看运营方案"],
];

export function OperatingStrategyPage() {
  const user = useAuthStore((s) => s.user);
  const shopId = useAuthStore((s) => s.currentShopId);
  const shops = useAuthStore((s) => s.shops);
  const scopeKey = operatingStrategyScope(user, shops, shopId);
  const activeScope = useOperatingStrategyStore((s) => s.scopeKey);
  const setScope = useOperatingStrategyStore((s) => s.setScope);
  useEffect(() => {
    setScope(scopeKey);
  }, [scopeKey, setScope]);
  if (!scopeKey) return <div className="p-8 text-muted-foreground">请先登录。</div>;
  if (scopeKey !== activeScope)
    return (
      <div className="p-8">
        <Spinner />
      </div>
    );
  return <OperatingStrategyData key={scopeKey} />;
}

function OperatingStrategyData() {
  const userId = useAuthStore((s) => s.user?.id);
  const shops = useAuthStore((s) => s.shops);
  const { data } = useQuery({
    ...strategySnapshotQuery(
      userId ?? "",
      shops.map((shop) => shop.shopId),
    ),
    enabled: Boolean(userId && shops.length > 0),
  });
  return <OperatingStrategyWizard snapshot={data} />;
}

function OperatingStrategyWizard({ snapshot }: { snapshot?: StrategySnapshot }) {
  const productTactics = STRATEGY_TACTIC_IDS.map((id) =>
    BUILTIN_STRATEGIES.find((strategy) => strategy.id === id),
  ).filter((strategy): strategy is Strategy => Boolean(strategy));
  const wizard = useStrategyWizard(
    productTactics.map((s) => s.id),
    snapshot,
  );
  const tactics =
    wizard.asin === STRATEGY_PRODUCT.asin
      ? productTactics
      : GENERIC_TACTIC_IDS.map((id) =>
          id === AUTO_EXPLORATION_STRATEGY.id
            ? AUTO_EXPLORATION_STRATEGY
            : BUILTIN_STRATEGIES.find((strategy) => strategy.id === id),
        ).filter((strategy): strategy is Strategy => Boolean(strategy));
  const navigate = useNavigate();
  const setWizardDirty = useOperatingStrategyStore((s) => s.setWizardDirty);
  useEffect(() => {
    setWizardDirty(wizard.dirty);
    return () => setWizardDirty(false);
  }, [wizard.dirty, setWizardDirty]);
  const [detail, setDetail] = useState<Strategy | null>(null);
  const [discard, setDiscard] = useState(false);
  const [leavingToCustom, setLeavingToCustom] = useState(false);
  const [keywordPreview, setKeywordPreview] = useState(false);
  const shopId = useAuthStore((s) => s.currentShopId);
  const productSnapshot = wizard.asin === STRATEGY_PRODUCT.asin ? snapshot : undefined;
  const blocker = useBlocker({
    shouldBlockFn: () => wizard.dirty,
    withResolver: true,
    enableBeforeUnload: wizard.dirty,
  });
  const interruptPending = wizard.interruptPending;
  useEffect(() => {
    if (blocker.status === "blocked") interruptPending();
  }, [blocker.status, interruptPending]);
  const step = wizard.stage === "research" ? 0 : wizard.stage === "complete" ? 2 : 1;
  const busy = wizard.busy !== null;
  const fileName =
    wizard.asin === STRATEGY_PRODUCT.asin
      ? STRATEGY_PRODUCT.reportFileName
      : `${wizard.asin}_运营报告.pdf`;
  const keywordFileName =
    wizard.asin === STRATEGY_PRODUCT.asin
      ? STRATEGY_PRODUCT.keywordFileName
      : `${wizard.asin}_关键词筛选.xlsx`;
  const choicesComplete = Boolean(
    wizard.settings.productStage &&
      wizard.settings.goal &&
      wizard.settings.mode &&
      (wizard.settings.goal !== SALES_GOAL || wizard.settings.rank),
  );
  const goalLabel =
    wizard.settings.goal === SALES_GOAL && wizard.settings.rank
      ? `${wizard.settings.goal} · ${wizard.settings.rank}`
      : wizard.settings.goal;

  function keepEditing() {
    setDiscard(false);
    setLeavingToCustom(false);
    blocker.reset?.();
  }
  function abandon() {
    wizard.reset();
    setDiscard(false);
    blocker.proceed?.();
  }
  function showLibrary() {
    void navigate({
      to: "/strategy-lab",
      search: { category: "ai", strategyId: wizard.created?.id },
    });
  }

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex flex-1 flex-col gap-6 px-8 py-7">
        <header className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold leading-8 tracking-tight">AI 运营策略</h1>
            <p className="mt-1.5 text-[13px] leading-[22px] text-muted-foreground">
              从赛道洞察到广告方案，为单个商品制定运营策略。
            </p>
          </div>
          <Badge variant="neutral">单商品流程</Badge>
        </header>
        <ol
          className="flex items-center gap-3 rounded-[10px] border border-line px-5 py-[18px]"
          aria-label="创建进度"
        >
          {STEPS.map(([title, subtitle], index) => (
            <li key={title} className="contents" aria-current={step === index ? "step" : undefined}>
              <div className="flex w-[280px] min-w-0 items-center gap-3">
                <span
                  className={cn(
                    "flex size-7 shrink-0 items-center justify-center rounded-full text-sm",
                    index <= step
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {index < step ? <IconCheck className="size-4" aria-hidden /> : index + 1}
                </span>
                <div className="flex flex-col gap-1.5">
                  <p
                    className={cn(
                      "text-sm leading-[22px]",
                      step === index ? "font-medium" : "text-muted-foreground",
                    )}
                  >
                    {title}
                  </p>
                  <p className="text-xs leading-5 text-muted-foreground">{subtitle}</p>
                </div>
              </div>
              {index < 2 && <div className="h-px w-[90px] bg-line" />}
            </li>
          ))}
        </ol>

        {wizard.stage === "research" && (
          <ResearchStep
            fileName={fileName}
            keywordFileName={keywordFileName}
            onPreviewKeywords={() => setKeywordPreview(true)}
            wizard={wizard}
          />
        )}

        {(wizard.stage === "settings" || wizard.stage === "plan") && (
          <>
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-2 text-sm font-semibold">{wizard.asin}</span>
                {wizard.stage === "plan" && (
                  <>
                    <Badge variant="neutral">{wizard.settings.productStage}</Badge>
                    <Badge variant="neutral">{goalLabel}</Badge>
                    <Badge variant="neutral">{wizard.settings.mode}</Badge>
                  </>
                )}
                <span className="w-full text-xs text-muted-foreground">
                  {wizard.stage === "plan" ? "商品策略决策" : "美国站"}
                </span>
              </div>
              <div className="flex gap-2">
                <ReportLink asin={wizard.asin} label="查看调研报告" />
                {wizard.stage === "plan" && (
                  <Button
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      wizard.setError("");
                      wizard.setStage("settings");
                    }}
                  >
                    修改设定
                  </Button>
                )}
              </div>
            </div>
            {wizard.stage === "settings" ? (
              <SettingsStep wizard={wizard} />
            ) : (
              <PlanStep
                onShowDetail={setDetail}
                productSnapshot={productSnapshot}
                tactics={tactics}
                wizard={wizard}
              />
            )}
          </>
        )}

        {wizard.stage === "complete" && wizard.created && (
          <CompleteStep created={wizard.created} wizard={wizard} />
        )}
      </div>

      <footer className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-line bg-surface px-8 py-4">
        <div className="flex items-center gap-2">
          {step === 1 && (
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => {
                wizard.setError("");
                wizard.setStage(wizard.stage === "plan" ? "settings" : "research");
              }}
            >
              <IconArrowLeft data-icon="inline-start" />
              上一步
            </Button>
          )}
          {wizard.dirty && (
            <Button
              variant="outline"
              onClick={() => {
                wizard.interruptPending();
                setDiscard(true);
              }}
            >
              取消
            </Button>
          )}
          {step === 0 && !wizard.reportReady && (
            <span className="text-[13px] text-muted-foreground">载入商品资料后继续</span>
          )}
        </div>
        <div className="flex gap-2">
          {wizard.stage === "research" && (
            <Button
              className="disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100"
              disabled={!wizard.reportReady || busy}
              onClick={() => {
                wizard.setError("");
                wizard.setStage("settings");
              }}
            >
              <IconArrowRight data-icon="inline-start" />
              下一步
            </Button>
          )}
          {wizard.stage === "settings" && (
            <Button type="submit" form="strategy-settings" disabled={!choicesComplete || busy}>
              <IconSparkles data-icon="inline-start" />
              {wizard.planSignature && !wizard.validPlan ? "重新生成策略决策" : "生成策略决策"}
            </Button>
          )}
          {wizard.stage === "plan" && (
            <>
              <Button
                variant="outline"
                disabled={busy || !wizard.validPlan}
                onClick={() => {
                  setLeavingToCustom(true);
                  void navigate({ to: "/campaigns/new", search: { shopId: shopId ?? undefined } });
                }}
              >
                自定义广告策略
              </Button>
              <Button disabled={busy || !wizard.validPlan} onClick={wizard.createStrategy}>
                {wizard.busy === "create" ? (
                  <Spinner data-icon="inline-start" />
                ) : (
                  <IconCheck data-icon="inline-start" />
                )}
                {wizard.busy === "create" ? "正在保存…" : "保存运营方案"}
              </Button>
            </>
          )}
          {wizard.stage === "complete" && (
            <>
              <Button variant="outline" onClick={wizard.reset}>
                继续创建
              </Button>
              <Button onClick={showLibrary}>
                查看策略库
                <IconArrowRight data-icon="inline-end" />
              </Button>
            </>
          )}
        </div>
      </footer>

      <AlertDialog
        open={discard || blocker.status === "blocked"}
        onOpenChange={(open) => {
          if (!open) keepEditing();
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {leavingToCustom ? "前往自定义广告策略？" : "放弃本次制定？"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {leavingToCustom
                ? "将前往创建广告活动页自定义投放，本次填写的商品、目标设定与方案不会保留。"
                : "当前目标设定与未保存方案将被清空。进行中的任务也会取消。"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={keepEditing}>
              继续编辑
            </Button>
            <Button onClick={abandon}>{leavingToCustom ? "确认前往" : "确认放弃"}</Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
      <BrandAuthorizationDialog
        open={wizard.unauthorized}
        onOpenChange={wizard.setUnauthorized}
        onAuthorize={() => {
          wizard.setUnauthorized(false);
          // Leaving to authorize is intentional; the discard prompt would only get in the way.
          void navigate({ to: "/settings", search: { tab: "amazon" }, ignoreBlocker: true });
        }}
      />
      <KeywordPreviewDialog
        open={keywordPreview}
        onOpenChange={setKeywordPreview}
        fileName={keywordFileName}
        productSnapshot={productSnapshot}
      />
      <StrategyDetailSheet strategy={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
